import { createContext, useContext } from "react";
import { ReadContractor, UpdateContractor } from "@/types/contractor";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/services/api";

export type ContractorHandlerKey =
  | "activate"
  | "deactivate"
  | "update"
  | "delete";

export type ContractorLoadingReason =
  | "fetching_contractor"
  | "updating_contractor"
  | "deleting_contractor"
  | "activating_contractor"
  | "deactivating_contractor"
  | "unknown";

export type ContractorLoadingState =
  | { isLoading: false }
  | { isLoading: true; reason: ContractorLoadingReason };

export interface MutationsObject {
  activateContractorMutation: UseMutationResult<
    ReadContractor,
    ApiError,
    undefined,
    unknown
  >;
  deactivateContractorMutation: UseMutationResult<
    ReadContractor,
    ApiError,
    undefined,
    unknown
  >;
  updateContractorMutation: UseMutationResult<
    ReadContractor,
    ApiError,
    UpdateContractor,
    unknown
  >;
  deleteContractorMutation: UseMutationResult<
    unknown,
    ApiError,
    undefined,
    unknown
  >;
}

export interface QueriesObject {
  contractorQuery: UseQueryResult<ReadContractor, Error>;
}

export interface ContractorContextType {
  query: UseQueryResult<ReadContractor, Error>;
  data?: ReadContractor;
  errorState: { error: Error | null; isQuery: boolean };
  loadingState: ContractorLoadingState;

  // Handlers
  handleActivate: () => void;
  handleDeactivate: () => void;
  handleUpdate: (updateData: UpdateContractor) => void;
  handleDelete: () => void;
  clearError: () => void;
  refreshContractor: () => void;
}

const ContractorContext = createContext<ContractorContextType>(
  {} as ContractorContextType,
);

export const useContractorContext = () => {
  const context = useContext(ContractorContext);
  if (!context) {
    throw new Error(
      "useContractorContext must be used within ContractorProvider",
    );
  }
  return context;
};

export { ContractorContext };
